/**
 * Ce que l'agent propose, et que seul le patron déclenche.
 *
 * L'agent ne tient jamais la plume d'une confirmation. Il désigne un document
 * par son identifiant — et c'est **le code** qui relit le document en base
 * pour écrire tout ce que le patron verra : numéro, client, adresse, montant.
 *
 * La règle vient d'un défaut qu'on aurait créé sans elle. Les verbes N3
 * portent des champs d'affichage (`invoiceNumber`, `recipient`,
 * `recipientEmail`, `amountTtc`) que l'écran remplit depuis la base. Laissés
 * au modèle, une hallucination produirait « Envoyer la facture FAC-12 à
 * Durand » sur une charge qui vise FAC-40 : le patron confirmerait une phrase
 * vraie pour un geste faux. Ici, le modèle ne fournit que des identifiants et
 * des choix (le ton d'une relance, un message) ; le reste est relu.
 *
 * L'argent passe par le même chemin, par décision du patron du 18 septembre :
 * un encaissement ou une dépense compris de travers ne s'écrit pas sans son
 * oui. `enregistrer_paiement` et `enregistrer_depense` restent N2 à l'écran —
 * c'est **l'agent** qui doit les faire confirmer.
 */
import { z } from "zod";
import { ActionLevel } from "../../levels.js";
import { DomainError } from "../../errors.js";
import { payloadHashOf } from "../../service.js";
import type { ActionDescription, Service, ServiceDeps, ServiceInput } from "../../service.js";
import type { ExecutionContext } from "../../context.js";
import type { ConfirmableAction, PendingAction, PrepareActionInput } from "../confirmations/services.js";
import type { InvoiceDetail } from "../invoices/types.js";
import type { QuoteDetail } from "../quotes/types.js";
import { clientDisplayName } from "../quotes/types.js";
import type { AgentTool } from "./services.js";
import { zodToToolSchema } from "./schema.js";

const eur = (n: number) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n);

const dateCourte = (iso: string) => {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

// ─── Une écriture N2 soumise à confirmation ───────────────────────────────

/**
 * Inscrit un verbe N2 au registre des confirmations, avec son propre résumé.
 *
 * `confirmable()` refuse un service N2, à raison : à l'écran, il s'exécute
 * directement. Celui-ci existe pour le seul cas où c'est **la porte** qui
 * exige le oui, et non le verbe. Le service n'est pas modifié : exécuté après
 * confirmation, il refait tous ses contrôles comme n'importe quel appel.
 */
export function confirmableWrite<I extends ServiceInput, O>(
  service: Service<I, O>,
  schema: z.ZodType<I, z.ZodTypeDef, unknown>,
  /**
   * Reçoit aussi la charge brute : les champs de lecture qu'une proposition
   * y a joints (numéro de facture, nom du client) ne passent pas le schéma du
   * service, mais le patron doit les lire.
   */
  summarize: (input: I, payload: Record<string, unknown>) => string,
): ConfirmableAction {
  if (service.level !== ActionLevel.ReversibleWrite) {
    throw new Error(`« ${service.name} » n'est pas une écriture N2.`);
  }
  return {
    name: service.name,
    describe: (payload): ActionDescription => {
      const input = schema.parse(payload);
      return {
        action: service.name,
        level: service.level,
        summary: summarize(input, (payload ?? {}) as Record<string, unknown>),
        // L'empreinte porte sur la charge entière, lectures comprises : ce que
        // le patron a lu fait partie de ce qu'il confirme.
        payloadHash: payloadHashOf(payload),
      };
    },
    run: (ctx, payload, _confirmationId, deps) => service(ctx, schema.parse(payload), deps),
  };
}

// ─── Les résumés de l'argent ──────────────────────────────────────────────

const MOYENS: Record<string, string> = {
  virement: "virement",
  cheque: "chèque",
  espece: "espèces",
  carte: "carte",
  prelevement: "prélèvement",
  delegation: "délégation de paiement au sous-traitant",
  autre: "autre moyen",
};

/**
 * Le paiement porte des champs de lecture (`invoiceNumber`, `clientName`)
 * que la proposition relit en base avant de préparer. Ils ne vont pas au
 * service — son schéma les écarte — mais ils entrent dans l'empreinte, donc
 * dans ce que le patron confirme.
 */
export function paymentSummary(
  input: { amount: number; paymentDate: string; paymentMethod?: string },
  read: { invoiceNumber?: unknown; clientName?: unknown } = {},
): string {
  const facture =
    typeof read.invoiceNumber === "string" ? `la facture ${read.invoiceNumber}` : "cette facture";
  const client = typeof read.clientName === "string" ? ` (${read.clientName})` : "";
  return (
    `Enregistrer un encaissement de ${eur(input.amount)} par ` +
    `${MOYENS[input.paymentMethod ?? "virement"] ?? input.paymentMethod} le ` +
    `${dateCourte(input.paymentDate)} sur ${facture}${client}. Le reste dû et les ` +
    `relances suivront.`
  );
}

export function expenseSummary(input: {
  description: string;
  amountHt: number;
  tvaRate?: number;
  expenseDate: string;
}): string {
  const tva = input.tvaRate ?? 20;
  return (
    `Enregistrer une dépense de ${eur(input.amountHt)} HT (TVA ${tva} %) le ` +
    `${dateCourte(input.expenseDate)} : « ${input.description} ».`
  );
}

// ─── Les propositions de l'agent ──────────────────────────────────────────

export interface ProposalDeps {
  lireFacture: Service<{ invoiceId: string; confirmationId?: string }, InvoiceDetail>;
  lireDevis: Service<{ quoteId: string; confirmationId?: string }, QuoteDetail>;
  preparerAction: Service<PrepareActionInput, PendingAction>;
}

/** Ce que l'agent reçoit en retour : de quoi dire au patron où cliquer. */
export interface Proposal {
  pendingActionId: string;
  summary: string;
  status: "en_attente_de_validation";
}

interface ProposalSpec<A extends z.ZodTypeAny> {
  /** Nom de l'outil, et nom de l'action inscrite : c'est le même verbe. */
  action: string;
  description: string;
  args: A;
  /** Relit la base et rend la charge complète du verbe. */
  build: (ctx: ExecutionContext, args: z.infer<A>, deps: ServiceDeps) => Promise<unknown>;
}

const uuid = (what: string) => z.string().uuid(`Identifiant de ${what} attendu`);
const message = z
  .string()
  .trim()
  .max(2000)
  .optional()
  .describe("Mot personnel ajouté à l'e-mail, seulement si le patron l'a dicté.");

function requireEmail(email: string | null | undefined, who: string): string {
  if (!email) {
    throw new DomainError(
      "invalid_input",
      `${who} n'a pas d'adresse e-mail sur sa fiche : impossible de lui écrire.`,
    );
  }
  return email;
}

function specs(d: ProposalDeps) {
  const facture = (ctx: ExecutionContext, invoiceId: string, deps: ServiceDeps) =>
    d.lireFacture(ctx, { invoiceId }, deps);
  const devis = (ctx: ExecutionContext, quoteId: string, deps: ServiceDeps) =>
    d.lireDevis(ctx, { quoteId }, deps);

  return [
    {
      action: "envoyer_devis",
      description:
        "Prépare l'envoi d'un devis au client par e-mail, PDF joint. Le patron devra valider.",
      args: z.object({ quoteId: uuid("devis"), message }),
      build: async (ctx, a, deps) => {
        const q = await devis(ctx, a.quoteId, deps);
        return {
          quoteId: q.id,
          quoteNumber: q.quoteNumber,
          recipient: clientDisplayName(q.client),
          recipientEmail: requireEmail(q.client?.email, "Ce client"),
          amountTtc: q.totals.totalTtc,
          ...(a.message ? { message: a.message } : {}),
        };
      },
    } satisfies ProposalSpec<z.ZodObject<{ quoteId: z.ZodString; message: typeof message }>>,
    {
      action: "relancer_devis",
      description:
        "Prépare la relance d'un devis envoyé resté sans réponse. Le patron devra valider.",
      args: z.object({ quoteId: uuid("devis"), message }),
      build: async (ctx, a, deps) => {
        const q = await devis(ctx, a.quoteId, deps);
        return {
          quoteId: q.id,
          quoteNumber: q.quoteNumber,
          recipient: clientDisplayName(q.client),
          recipientEmail: requireEmail(q.client?.email, "Ce client"),
          amountTtc: q.totals.totalTtc,
          validUntil: null,
          ...(a.message ? { message: a.message } : {}),
        };
      },
    } satisfies ProposalSpec<z.ZodObject<{ quoteId: z.ZodString; message: typeof message }>>,
    {
      action: "envoyer_facture",
      description:
        "Prépare l'envoi d'une facture au client par e-mail, PDF joint. Une facture en " +
        "brouillon passe alors « envoyée ». Le patron devra valider.",
      args: z.object({ invoiceId: uuid("facture"), message }),
      build: async (ctx, a, deps) => {
        const f = await facture(ctx, a.invoiceId, deps);
        return {
          invoiceId: f.id,
          invoiceNumber: f.invoiceNumber,
          recipient: clientDisplayName(f.client),
          recipientEmail: requireEmail(f.client?.email, "Ce client"),
          amountTtc: f.totalTtc,
          isDraft: f.status === "draft",
          ...(a.message ? { message: a.message } : {}),
        };
      },
    } satisfies ProposalSpec<z.ZodObject<{ invoiceId: z.ZodString; message: typeof message }>>,
    {
      action: "relancer_facture",
      description:
        "Prépare une relance de paiement. level : 1 rappel avant échéance, 2 relance " +
        "amicale, 3 relance ferme, 4 mise en demeure. Le patron devra valider.",
      args: z.object({
        invoiceId: uuid("facture"),
        level: z.number().int().min(1).max(4).describe("1 à 4, du plus doux au plus ferme"),
      }),
      build: async (ctx, a, deps) => {
        const f = await facture(ctx, a.invoiceId, deps);
        if (f.balance.isSettled) {
          throw new DomainError("conflict", `La facture ${f.invoiceNumber} est déjà réglée.`);
        }
        return {
          invoiceId: f.id,
          level: a.level,
          invoiceNumber: f.invoiceNumber,
          recipient: clientDisplayName(f.client),
          amountDue: f.balance.remaining,
        };
      },
    } satisfies ProposalSpec<z.ZodObject<{ invoiceId: z.ZodString; level: z.ZodNumber }>>,
    {
      action: "enregistrer_paiement",
      description:
        "Prépare l'enregistrement d'un paiement reçu sur une facture. Le patron devra valider.",
      args: z.object({
        invoiceId: uuid("facture"),
        amount: z.number().positive().describe("Montant encaissé, en euros"),
        paymentDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).describe("AAAA-MM-JJ"),
        paymentMethod: z
          .enum(["virement", "cheque", "espece", "carte", "prelevement", "autre"])
          .default("virement"),
      }),
      build: async (ctx, a, deps) => {
        const f = await facture(ctx, a.invoiceId, deps);
        return {
          ...a,
          invoiceId: f.id,
          invoiceNumber: f.invoiceNumber,
          clientName: clientDisplayName(f.client),
        };
      },
    } satisfies ProposalSpec<z.ZodTypeAny>,
    {
      action: "enregistrer_depense",
      description:
        "Prépare l'enregistrement d'une dépense (achat, carburant, sous-traitance…). " +
        "Le patron devra valider.",
      args: z.object({
        description: z.string().trim().min(1),
        amountHt: z.number().positive().describe("Montant hors taxes, en euros"),
        tvaRate: z.number().min(0).max(100).default(20),
        expenseDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).describe("AAAA-MM-JJ"),
        projectId: uuid("chantier").optional().describe("Chantier imputé, s'il est connu"),
      }),
      build: async (_ctx, a) => a,
    } satisfies ProposalSpec<z.ZodTypeAny>,
  ] as ProposalSpec<z.ZodTypeAny>[];
}

/** Noms des actions que l'agent peut proposer. Tous doivent être au registre. */
export const AGENT_PROPOSALS = [
  "envoyer_devis",
  "relancer_devis",
  "envoyer_facture",
  "relancer_facture",
  "enregistrer_paiement",
  "enregistrer_depense",
] as const;

/**
 * Les outils de proposition.
 *
 * Chacun relit, construit la charge, et appelle `preparer_action` — qui la
 * revalide avec le schéma du verbe et en tire le résumé par le registre. Rien
 * ne s'exécute ici : l'outil rend un identifiant de demande et le résumé que
 * le patron lira.
 */
export function proposalTools(d: ProposalDeps): AgentTool[] {
  return specs(d).map((spec) => ({
    name: spec.action,
    description: spec.description,
    inputSchema: zodToToolSchema(spec.args),
    level: ActionLevel.ReversibleWrite,
    kind: "proposal" as const,
    run: async (ctx, input, deps): Promise<Proposal> => {
      const args = spec.args.parse(input ?? {});
      const payload = await spec.build(ctx, args, deps);
      const pending = await d.preparerAction(ctx, { action: spec.action, payload }, deps);
      return {
        pendingActionId: pending.id,
        summary: pending.summary,
        status: "en_attente_de_validation",
      };
    },
  }));
}
